/**
 * `orgApi` — RTK Query endpoints for the organizations service (design §13.2).
 *
 * Injected into the shared `apiSlice`; the shell store is never edited. Every
 * endpoint answers `{ data: ... }`, so `transformResponse` unwraps the envelope
 * at the boundary.
 *
 * Tag strategy: the shell's `apiSlice` is created without a `tagTypes` list, so
 * per-id tag objects are rejected by the type system and only **bare string tags**
 * are available (`OrganizationList`, `Organization`, `Members`, `OrgProjects`).
 * That is equivalent in P1 because a page mounts exactly one organization (or one
 * project) at a time, so invalidating the type refreshes precisely the active
 * view — and inactive cached queries simply get evicted.
 *
 * Member mutations also drop every cached policy verdict for `org:${id}`
 * (§14.4): who is in the org changes what the caller may do next, and a 403 from
 * the server always re-opens the question.
 */

import { apiSlice } from '@/store/apiSlice';
import { invalidateResource } from '@/features/permissions/can';
import { orgResource } from '@/features/permissions/model';
import type {
  AddMemberRequest,
  DataEnvelope,
  OrgMember,
  Organization,
  OrganizationFull,
  OrgProject,
} from '../types';

/** Unwraps the `{ data: ... }` envelope the organizations/roles/projects services answer with. */
function unwrap<T>(raw: DataEnvelope<T> | T | undefined): T | undefined {
  if (raw && typeof raw === 'object' && 'data' in (raw as DataEnvelope<T>)) {
    return (raw as DataEnvelope<T>).data;
  }
  return raw as T | undefined;
}

/**
 * The shell `apiSlice` is created without `tagTypes`, so its tag union is `never`.
 * `enhanceEndpoints({addTagTypes})` is the documented RTK 2 way to introduce a
 * feature's own tag names without editing the shell — it returns the same API with
 * the tags added, which is what makes per-id invalidation typecheck.
 */
const orgTaggedApi = apiSlice.enhanceEndpoints({
  addTagTypes: ['OrganizationList', 'Organization', 'Members', 'OrgProjects'],
});

export const orgApi = orgTaggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /** `GET /organizations` → the caller's organizations (no query params). */
    getOrganizations: builder.query<Organization[], void>({
      query: () => ({ url: 'organizations' }),
      transformResponse: (raw: DataEnvelope<Organization[]> | Organization[]) =>
        unwrap<Organization[]>(raw) ?? [],
      providesTags: [{ type: 'OrganizationList', id: 'LIST' }],
    }),

    /**
     * `GET /organizations/:id` — the organization document. It carries **no**
     * members: the design's assumption A1 (`GET .../id` returns `members`) does
     * not hold against the service, whose members live on `/full`. Detail header
     * and tab shell read this endpoint; the members table reads
     * `getOrganizationFull`. No endpoint was invented either way.
     */
    getOrganization: builder.query<Organization | undefined, string>({
      query: (orgId) => ({ url: `organizations/${orgId}` }),
      transformResponse: (raw: DataEnvelope<Organization>) => unwrap<Organization>(raw),
      providesTags: (_result, _error, orgId) => [{ type: 'Organization', id: orgId }],
    }),

    /** `GET /organizations/:id/full` → `{ organization, members, groups, roles }`. */
    getOrganizationFull: builder.query<OrganizationFull | undefined, string>({
      query: (orgId) => ({ url: `organizations/${orgId}/full` }),
      transformResponse: (raw: DataEnvelope<OrganizationFull>) => unwrap<OrganizationFull>(raw),
      providesTags: (_result, _error, orgId) => [
        { type: 'Organization', id: orgId },
        { type: 'Members', id: orgId },
      ],
    }),

    /** `POST /organizations/:id/members` (`AddOrgMemberDto`: `{userId, orgRole, roleIds?}`). */
    addMember: builder.mutation<OrgMember | undefined, AddMemberRequest & { orgId: string }>({
      query: ({ orgId, ...body }) => ({
        url: `organizations/${orgId}/members`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw: DataEnvelope<OrgMember>) => unwrap<OrgMember>(raw),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'Members', id: orgId },
        { type: 'Organization', id: orgId },
        { type: 'OrganizationList', id: 'LIST' },
      ],
      async onQueryStarted({ orgId }, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          /* 403/5xx surface through the caller's toast (§13.3) */
        } finally {
          invalidateResource(orgResource(orgId));
        }
      },
    }),

    /** `DELETE /organizations/:id/members/:userId` → 204 No Content. */
    removeMember: builder.mutation<void, { orgId: string; userId: string }>({
      query: ({ orgId, userId }) => ({
        url: `organizations/${orgId}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'Members', id: orgId },
        { type: 'Organization', id: orgId },
        { type: 'OrganizationList', id: 'LIST' },
      ],
      async onQueryStarted({ orgId }, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          /* handled by the caller's toast */
        } finally {
          invalidateResource(orgResource(orgId));
        }
      },
    }),

    /** `GET /organizations/:id/projects` — the org-scoped project list. */
    getOrgProjects: builder.query<OrgProject[], string>({
      query: (orgId) => ({ url: `organizations/${orgId}/projects` }),
      transformResponse: (raw: DataEnvelope<OrgProject[]> | OrgProject[]) =>
        unwrap<OrgProject[]>(raw) ?? [],
      providesTags: (_result, _error, orgId) => [{ type: 'OrgProjects', id: orgId }],
    }),
  }),
});

export const {
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useGetOrganizationFullQuery,
  useAddMemberMutation,
  useRemoveMemberMutation,
  useGetOrgProjectsQuery,
} = orgApi;
